import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { merge as xMerge } from 'lodash';

import {
    PLAN_VIEW_MOUSEMOVE_EVENT_TYPE,
    PLAN_VIEW_PAINT_EVENT_TYPE,
    PLAN_VIEW_RESIZE_EVENT_TYPE,
    PLAN_VIEW_CLICK_EVENT_TYPE,
    PlanViewRepaintEvent,
    PlanViewEvent,
} from './PlanViewAdvanced2.PlanViewEvents';

const PLAN_VIEW_VIEWPOINT_CLICKED_EVENT_TYPE = "viewpoint_clicked";

class PlanViewViewpointClicked extends PlanViewEvent {
    constructor(info) {
        super(PLAN_VIEW_VIEWPOINT_CLICKED_EVENT_TYPE);
        xMerge(this, info);
    }
}


function PlanViewAdvanced2ViewPointSelect(props, ref)
{
    const {
        planView,
        viewPoints,
        selectedViewpointId,
        yaw,
        hfov,
        zIndexBase,
    } = props;

    const canvasRef = useRef();


    const dispatchViewpointClicked = opts => {
        planView?.dispatchEvent(new PlanViewViewpointClicked(opts));
    }


    const planViewMouseMove = useCallback((e) => {

        let cursorShape = "auto";

        const overPointIdx = viewPoints?.findIndex(vp => {
            const dx = vp.x - e.scaledX;
            const dy = vp.y - e.scaledY;
            const d = Math.sqrt(dx * dx + dy * dy) * e.scale;
            return d < e.viewPointRadiusPx;
        })
        if (overPointIdx > -1) {
            cursorShape = "pointer";
        }
        document.body.style.cursor = cursorShape;
    }, [viewPoints]);


    const planViewClick = useCallback((e) => {
        const overPointIdx = viewPoints?.findIndex(vp => {
            const dx = vp.x - e.scaledX;
            const dy = vp.y - e.scaledY;
            const d = Math.sqrt(dx * dx + dy * dy) * e.scale;
            return d < e.viewPointRadiusPx;
        })
        if (overPointIdx > -1) {
            dispatchViewpointClicked({viewPointId:viewPoints[overPointIdx].id});
        }
    }, [viewPoints]);

    const fToRad = Math.PI / 180.0;
    const frustumSize = 64;

    const drawFrustumAt = function(canvas, viewPoint, viewTrafo, hfov, camViewAngle, color = [51,88,255]) {
        //const drawFrustumAt = function(canvas, viewPoint, viewTrafo, hfov, camViewAngle, color = [64,160,192]) {
        const svp = viewPoint.matrixTransform(viewTrafo);

        const a0 = fToRad * (camViewAngle - hfov / 2);
        const a1 = fToRad * (camViewAngle + hfov / 2);
        const cx0 = svp.x + (Math.cos(a0) * frustumSize);
        const cy0 = svp.y + (Math.sin(a0) * frustumSize);
        const cx1 = svp.x + (Math.cos(a1) * frustumSize);
        const cy1 = svp.y + (Math.sin(a1) * frustumSize);

        const camFrustum = new Path2D();
        camFrustum.moveTo(svp.x, svp.y);
        camFrustum.arc(svp.x, svp.y, frustumSize,a0,a1);
        camFrustum.closePath();

        const camViewGrad = canvas.createRadialGradient(
            svp.x, svp.y,0,
            svp.x, svp.y,
            frustumSize);
        camViewGrad.addColorStop(0.0, `rgba(${color[0]},${color[1]},${color[2]},0.75)`);
        camViewGrad.addColorStop(0.3, `rgba(${color[0]},${color[1]},${color[2]},0.45)`);
        camViewGrad.addColorStop(1.0, `rgba(${color[0]},${color[1]},${color[2]},0.20)`);

        canvas.shadowBlur = 5;
        canvas.shadowColor = `rgba(${color[0]},${color[1]},${color[2]},1)`;
        canvas.fillStyle = camViewGrad;
        canvas.fill(camFrustum);
        canvas.shadowBlur = canvas.shadowOffsetX = canvas.shadowOffsetY = 0;


        canvas.strokeStyle = `rgba(${color[0]},${color[1]},${color[2]},0.75)`;
        //canvas.strokeStyle = '#000000';
        canvas.lineWidth = 1.25;
        canvas.lineCap = 'round';
        canvas.beginPath();
        canvas.moveTo(cx0,cy0);
        canvas.lineTo(svp.x, svp.y);
        canvas.lineTo(cx1,cy1);
        canvas.stroke();
    }


    const planViewPaint = useCallback((e) => {
        const drawCtx = canvasRef.current.getContext("2d");

        drawCtx.setTransform(1, 0, 0, 1, 0, 0);
        drawCtx.clearRect(0, 0, drawCtx.canvas.width, drawCtx.canvas.height);

        const tr = e.viewTransform;

        viewPoints.forEach((vp) => {
            if (vp.id === selectedViewpointId) {
                drawFrustumAt(drawCtx, vp, tr, hfov, yaw);
                e.drawViewpoint(drawCtx, vp, tr, { drawAsSelected: true });
            }
        });

    }, [viewPoints, selectedViewpointId, yaw, hfov]);


    const planViewResize = (e) => {
        ref.current.width = e.width;
        ref.current.height = e.height;
        canvasRef.current.width = e.width;
        canvasRef.current.height = e.height;
    };


    useEffect(() => {
        if (!(planView instanceof HTMLElement))
            return;

        planView.addEventListener(PLAN_VIEW_MOUSEMOVE_EVENT_TYPE, planViewMouseMove);
        planView.addEventListener(PLAN_VIEW_CLICK_EVENT_TYPE, planViewClick);
        planView.addEventListener(PLAN_VIEW_PAINT_EVENT_TYPE, planViewPaint);
        planView.addEventListener(PLAN_VIEW_RESIZE_EVENT_TYPE, planViewResize);

        const rc = planView.getBoundingClientRect();
        ref.current.width = rc.width;
        ref.current.height = rc.height;
        canvasRef.current.width = rc.width;
        canvasRef.current.height = rc.height;

        planView.dispatchEvent(new PlanViewRepaintEvent());

        return () => {
            planView.removeEventListener(PLAN_VIEW_MOUSEMOVE_EVENT_TYPE, planViewMouseMove);
            planView.removeEventListener(PLAN_VIEW_CLICK_EVENT_TYPE, planViewClick);
            planView.removeEventListener(PLAN_VIEW_PAINT_EVENT_TYPE, planViewPaint);
            planView.removeEventListener(PLAN_VIEW_RESIZE_EVENT_TYPE, planViewResize);
        };
    }, [planView, planViewMouseMove, planViewClick, planViewPaint]);


    useEffect(() => {
        planView?.dispatchEvent(new PlanViewRepaintEvent());
    }, [selectedViewpointId]);


    return (
        <section ref={ref}
                 style={{
                     position:'absolute',
                     left:0,
                     right:0,
                     bottom:0,
                     top:0,
                     zIndex:zIndexBase,
                     pointerEvents:'none'
                 }}>
            <canvas ref={canvasRef}
                    width={1}
                    height={1}
                    style={{
                        position:'absolute',
                        left:0,
                        top:0,
                        zIndex:zIndexBase+1,
                        pointerEvents:'none'
                    }}>
            </canvas>
        </section>
    );
}


export default React.forwardRef(PlanViewAdvanced2ViewPointSelect);

export {
    PLAN_VIEW_VIEWPOINT_CLICKED_EVENT_TYPE,
    PlanViewViewpointClicked
}


PlanViewAdvanced2ViewPointSelect.propTypes = {
    planView:PropTypes.object.isRequired,
    viewPoints:PropTypes.array.isRequired,
    selectedViewpointId:PropTypes.number,
    yaw:PropTypes.number,
    hfov:PropTypes.number,
    zIndexBase:PropTypes.number,
}
